import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import twemoji from 'twemoji';
import { ChevronDown, Copy, Check } from 'lucide-react';
import s from './MiniFigma.module.sass';

// Импорт иконок для тарифов
import tarif1Icon from '../../img/iconsas/1tarif.png';
import tarif2Icon from '../../img/iconsas/2tarif.png';
import tarif3Icon from '../../img/iconsas/3tarif.png';
import tarif4Icon from '../../img/iconsas/4tarif.png';
import viptarifIcon from '../../img/iconsas/viptarif.png';

const MiniFigma = ({ siteSettings, isNavbarCollapsed, currentUserGeneratedAmounts = null, variant = 'classic', tabsPortalNode = null }) => {
    const isNova = variant === 'nova';
    const [activeSection, setActiveSection] = useState(null);
    const [activeTariff, setActiveTariff] = useState('💰 1 ТАРИФ'); // Используется только для замены переменных по умолчанию
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [collapsedSubsections, setCollapsedSubsections] = useState({});
    const [maxCardHeight, setMaxCardHeight] = useState(null);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [showingTariffSelection, setShowingTariffSelection] = useState({}); // ID карточек с выбором тарифов
    const [closingTariffSelection, setClosingTariffSelection] = useState({}); // ID карточек с анимацией закрытия
    const gridRef = useRef(null);
    const rootRef = useRef(null);
    // Память скролла по вкладкам (Nova): при возврате на вкладку
    // восстанавливаем позицию, где пользователь был до переключения
    const tabScrollMemory = useRef({});

    // Ближайший скроллируемый предок (в Nova-шите это .body листа)
    const getScrollParent = useCallback(() => {
        let el = rootRef.current?.parentElement;
        while (el && el !== document.body) {
            const style = window.getComputedStyle(el);
            if (/(auto|scroll)/.test(style.overflowY)) return el;
            el = el.parentElement;
        }
        return null;
    }, []);

    // Используем useMemo чтобы sections не пересоздавался на каждом рендере
    const sections = useMemo(() => {
        return siteSettings?.miniFigma?.sections || [];
    }, [siteSettings?.miniFigma?.sections]);
    
    const enabledSections = useMemo(() => {
        return sections.filter(section => section.enabled !== false);
    }, [sections]);

    // Инициализация состояния групп на основе настройки
    useEffect(() => {
        // Если sections пустой, не обновляем состояние
        if (!sections || sections.length === 0) {
            return;
        }
        
        const shouldCollapseByDefault = siteSettings?.miniFigma?.groupsCollapsedByDefault ?? true;
        
        const initialCollapsedState = {};
        
        // Проходим по всем разделам и группам
        sections.forEach(section => {
            if (section.messages) {
                section.messages.forEach(item => {
                    if (item.type === 'group') {
                        // Если группа имеет флаг alwaysOpen, она по умолчанию раскрыта
                        if (item.alwaysOpen) {
                            initialCollapsedState[item.id] = false;
                        } else if (shouldCollapseByDefault) {
                            initialCollapsedState[item.id] = true;
                        } else {
                            initialCollapsedState[item.id] = false;
                        }
                    }
                });
            }
        });
        
        // Обновляем состояние только если оно действительно изменилось
        setCollapsedGroups(prev => {
            // Проверяем, изменилось ли состояние
            const hasChanged = Object.keys(initialCollapsedState).some(id => 
                prev[id] !== initialCollapsedState[id]
            ) || Object.keys(prev).some(id => 
                !(id in initialCollapsedState)
            );
            
            if (!hasChanged && Object.keys(prev).length === Object.keys(initialCollapsedState).length) {
                return prev; // Не обновляем, если состояние не изменилось
            }
            
            return initialCollapsedState;
        });
    }, [siteSettings?.miniFigma?.groupsCollapsedByDefault, sections]);

    // Обработчик прокрутки для показа кнопки "Наверх"
    useEffect(() => {
        const handleScroll = () => {
            // Находим MiniFigma компонент
            const miniFigmaElement = document.querySelector('[class*="miniFigma"]');
            if (miniFigmaElement) {
                const rect = miniFigmaElement.getBoundingClientRect();
                // Показываем кнопку когда MiniFigma достигает верха экрана (табы становятся sticky)
                setShowScrollToTop(rect.top <= 0);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Функция прокрутки наверх
    const scrollToTop = useCallback(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    // Функция плавного переключения табов
    const handleTabChange = useCallback((sectionId) => {
        if (sectionId === activeSection || isTransitioning) return;

        // Запоминаем, где пользователь был на текущей вкладке
        if (isNova) {
            const scroller = getScrollParent();
            if (scroller && activeSection) {
                tabScrollMemory.current[activeSection] = scroller.scrollTop;
            }
        }

        setIsTransitioning(true);
        
        // Небольшая задержка для плавности
        setTimeout(() => {
            setActiveSection(sectionId);
            setIsTransitioning(false);
        }, 150);
    }, [activeSection, isTransitioning, isNova, getScrollParent]);

    // После смены вкладки возвращаем сохранённый скролл (или наверх)
    useLayoutEffect(() => {
        if (!isNova || !activeSection) return;
        const scroller = getScrollParent();
        if (scroller) {
            scroller.scrollTop = tabScrollMemory.current[activeSection] ?? 0;
        }
    }, [activeSection, isNova, getScrollParent]);

    // Функция для переключения состояния группы с плавной анимацией
    const toggleGroupCollapse = useCallback((groupId) => {
        // Nova: анимация через grid-template-rows (0fr↔1fr) в CSS,
        // JS-манипуляции с max-height не нужны
        if (isNova) {
            setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
            return;
        }
        
        const groupElement = document.querySelector(`[data-group-id="${groupId}"]`);
        const groupMessagesElement = groupElement?.querySelector(`.${s.groupMessages.split(' ')[0]}`);
        
        if (groupMessagesElement) {
            const isCurrentlyCollapsed = collapsedGroups[groupId];
            
            if (isCurrentlyCollapsed) {
                // Разворачиваем: убираем класс collapsing и позволяем элементу развернуться
                groupMessagesElement.classList.remove(s.collapsing);
                
                // Устанавливаем высоту для плавной анимации
                requestAnimationFrame(() => {
                    const scrollHeight = groupMessagesElement.scrollHeight;
                    groupMessagesElement.style.maxHeight = `${scrollHeight}px`;
                    
                    // После завершения анимации убираем фиксированную высоту
                    setTimeout(() => {
                        if (!collapsedGroups[groupId]) {
                            groupMessagesElement.style.maxHeight = '2000px';
                        }
                    }, 350);
                });
            } else {
                // Сворачиваем: фиксируем текущую высоту, затем анимируем к 0
                const scrollHeight = groupMessagesElement.scrollHeight;
                groupMessagesElement.style.maxHeight = `${scrollHeight}px`;
                
                // Используем requestAnimationFrame для плавности
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        groupMessagesElement.classList.add(s.collapsing);
                    });
                });
            }
        }
        
        setCollapsedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    }, [collapsedGroups, s.collapsing, s.groupMessages, isNova]);

    // Функция для преобразования эмодзи в SVG с помощью Twemoji
    const convertEmojiToSvg = useCallback((emoji) => {
        if (!emoji) return null;
        
        // Конвертируем эмодзи в SVG с помощью Twemoji
        const svgString = twemoji.parse(emoji, {
            folder: 'svg',
            ext: '.svg',
            base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/'
        });
        
        return svgString;
    }, []);

    // Функция для извлечения первого эмодзи из текста и удаления его из текста
    const extractFirstEmoji = useCallback((text) => {
        if (!text) return { emoji: null, textWithoutEmoji: text };
        
        // Более точное регулярное выражение для составных эмодзи
        // Включает ZWJ sequences (Zero Width Joiner) для правильного распознавания составных эмодзи
        const emojiRegex = /(?:(?:\ud83c[\udf00-\udfff])|(?:\ud83d[\udc00-\ude4f\ude80-\udeff])|(?:\ud83e[\udd00-\uddff\ude70-\udef0])|(?:\ud83f[\udde0-\uddff]))(?:\u200d(?:(?:\ud83c[\udf00-\udfff])|(?:\ud83d[\udc00-\ude4f\ude80-\udeff])|(?:\ud83e[\udd00-\uddff\ude70-\udef0])|(?:\ud83f[\udde0-\uddff])|[\u2600-\u27bf]|[\u2b00-\u2bff]|[\u3000-\u303f]|[\u3200-\u32ff]))*|(?:\ud83c[\udde0-\uddff][\udde0-\uddff])|(?:[\u2600-\u27bf])|(?:[\u2b00-\u2bff])|(?:[\u3000-\u303f])|(?:[\u3200-\u32ff])|(?:[\u0023\u002a\u0030-\u0039]\ufe0f?\u20e3)|(?:\u00a9\ufe0f?)|(?:\u00ae\ufe0f?)|(?:\u203c\ufe0f?)|(?:\u2049\ufe0f?)|(?:\u2122\ufe0f?)|(?:\u2139\ufe0f?)|(?:[\u2194-\u2199]\ufe0f?)|(?:\u21a9\ufe0f?)|(?:\u21aa\ufe0f?)|(?:[\u231a-\u231b])|(?:\u2328\ufe0f?)|(?:\u23cf\ufe0f?)|(?:[\u23e9-\u23f3])|(?:[\u23f8-\u23fa])|(?:\u24c2\ufe0f?)|(?:[\u25aa-\u25ab])|(?:\u25b6\ufe0f?)|(?:\u25c0\ufe0f?)|(?:[\u25fb-\u25fe])|(?:[\u2600-\u2604]\ufe0f?)|(?:\u260e\ufe0f?)|(?:\u2611\ufe0f?)|(?:[\u2614-\u2615])|(?:\u2618\ufe0f?)|(?:\u261d(?:\ud83c[\udffb-\udfff])?)|(?:[\u2620\u2622-\u2623\u2626\u262a\u262e-\u262f\u2638-\u263a\u2640\u2642\u2648-\u2653\u265f-\u2660\u2663\u2665-\u2666\u2668\u267b\u267e-\u267f\u2692-\u2697\u2699\u269b-\u269c\u26a0-\u26a1\u26a7\u26aa-\u26ab\u26b0-\u26b1\u26bd-\u26be\u26c4-\u26c5\u26c8\u26ce-\u26cf\u26d1\u26d3-\u26d4\u26e9-\u26ea\u26f0-\u26f5\u26f7-\u26fa\u26fd\u2702\u2705\u2708-\u270d\u270f\u2712\u2714\u2716\u271d\u2721\u2728\u2733-\u2734\u2744\u2747\u274c\u274e\u2753-\u2755\u2757\u2763-\u2764\u2795-\u2797\u27a1\u27b0\u27bf\u2934-\u2935\u2b05-\u2b07\u2b1b-\u2b1c\u2b50\u2b55\u3030\u303d\u3297\u3299]\ufe0f?)(?:\u200d(?:(?:\ud83c[\udf00-\udfff])|(?:\ud83d[\udc00-\ude4f\ude80-\udeff])|(?:\ud83e[\udd00-\uddff\ude70-\udef0])|(?:\ud83f[\udde0-\uddff])|[\u2600-\u27bf]|[\u2b00-\u2bff]|[\u3000-\u303f]|[\u3200-\u32ff]))*/g;
        
        // Ищем эмодзи в тексте
        const matches = text.match(emojiRegex);
        
        if (!matches || matches.length === 0) {
            return { emoji: null, textWithoutEmoji: text };
        }
        
        // Берем первое совпадение (самое длинное составное эмодзи)
        let firstEmoji = matches[0];
        
        // Дополнительная проверка для составных эмодзи с модификаторами
        // Ищем все возможные части эмодзи в начале строки
        const extendedEmojiRegex = /^(?:(?:\ud83c[\udf00-\udfff])|(?:\ud83d[\udc00-\ude4f\ude80-\udeff])|(?:\ud83e[\udd00-\uddff\ude70-\udef0])|(?:\ud83f[\udde0-\uddff])|[\u2600-\u27bf]|[\u2b00-\u2bff]|[\u3000-\u303f]|[\u3200-\u32ff])(?:[\ufe0e\ufe0f])?(?:\u200d(?:(?:\ud83c[\udf00-\udfff])|(?:\ud83d[\udc00-\ude4f\ude80-\udeff])|(?:\ud83e[\udd00-\uddff\ude70-\udef0])|(?:\ud83f[\udde0-\uddff])|[\u2600-\u27bf]|[\u2b00-\u2bff]|[\u3000-\u303f]|[\u3200-\u32ff])(?:[\ufe0e\ufe0f])?)*/;
        
        const extendedMatch = text.match(extendedEmojiRegex);
        if (extendedMatch && extendedMatch[0].length > firstEmoji.length) {
            firstEmoji = extendedMatch[0];
        }
        
        // Удаляем найденный эмодзи из текста
        const textWithoutEmoji = text.replace(firstEmoji, '').trim();
        
        return { emoji: firstEmoji, textWithoutEmoji };
    }, []);


    // Функция для получения иконки тарифа
    const getTariffIcon = (tariff) => {
        const iconMap = {
            '💰 1 ТАРИФ': tarif1Icon,
            '❗❗ 2 ТАРИФ': tarif2Icon,
            '❗❗❗ 3 ТАРИФ': tarif3Icon,
            '❗❗❗❗ 4 ТАРИФ': tarif4Icon,
            '⚠️ VIP ТАРИФ': viptarifIcon
        };
        return iconMap[tariff] || tarif1Icon;
    };

    // Функция для получения названия тарифа без эмодзи
    const getTariffName = (tariff) => {
        return tariff.replace(/^[💰❗⚠️\s]+/, '').trim();
    };

    // Nova: короткая подпись тарифа («1», «2», … «VIP») — кнопки
    // помещаются одним рядом даже в узкой карточке
    const getTariffShortName = (tariff) => {
        if (tariff.includes('VIP')) return 'VIP';
        const m = tariff.match(/\d+/);
        return m ? m[0] : getTariffName(tariff);
    };

    // Nova: hairline-разделитель на каждой границе
    // «группа ↔ одиночные карточки» (в том числе для единичной карточки)
    const novaDividerBefore = (arr, i) => {
        if (!isNova || i === 0) return false;
        const prevGroup = arr[i - 1]?.type === 'group';
        const curGroup = arr[i]?.type === 'group';
        return prevGroup !== curGroup;
    };

    // Функция для получения цвета тарифа (как в админке)
    const getTariffColor = (tariff) => {
        if (tariff.includes('VIP')) return '#ef4444'; // красный
        if (tariff.includes('1 ТАРИФ')) return '#22c55e'; // зеленый
        if (tariff.includes('2 ТАРИФ')) return '#f97316'; // оранжевый
        if (tariff.includes('3 ТАРИФ')) return '#3b82f6'; // синий
        if (tariff.includes('4 ТАРИФ')) return '#9333ea'; // фиолетовый
        return '#1e40af'; // дефолтный синий
    };

    // Функция для получения бледного фона тарифа
    const getTariffBackgroundColor = (tariff) => {
        if (tariff.includes('VIP')) return 'rgba(239, 68, 68, 0.1)'; // бледно-красный
        if (tariff.includes('1 ТАРИФ')) return 'rgba(34, 197, 94, 0.1)'; // бледно-зеленый
        if (tariff.includes('2 ТАРИФ')) return 'rgba(249, 115, 22, 0.1)'; // бледно-оранжевый
        if (tariff.includes('3 ТАРИФ')) return 'rgba(59, 130, 246, 0.1)'; // бледно-синий
        if (tariff.includes('4 ТАРИФ')) return 'rgba(147, 51, 234, 0.1)'; // бледно-фиолетовый
        return 'rgba(30, 64, 175, 0.1)'; // дефолтный бледно-синий
    };

    // Функция для выравнивания высоты карточек по строкам
    const calculateMaxHeight = useCallback(() => {
        // Nova: карточки — компактные чипы по контенту, высоты не выравниваем
        if (isNova) return;
        if (!gridRef.current) return;
        
        // Сбрасываем высоту для всех карточек
        const allCards = gridRef.current.querySelectorAll(`.${s.messageCard}`);
        allCards.forEach(card => {
            card.style.height = 'auto';
            card.style.minHeight = 'auto';
        });
        
        // Принудительно перерисовываем для точного измерения
        // eslint-disable-next-line no-unused-expressions
        gridRef.current.offsetHeight;
        
        // Функция для группировки карточек по строкам и выравнивания высоты
        const alignCardsInRows = (cards) => {
            if (cards.length === 0) return;
            
            // Группируем карточки по строкам на основе их позиции top
            const cardsByRow = new Map();
            
            cards.forEach(card => {
                const rect = card.getBoundingClientRect();
                const top = Math.round(rect.top);
                
                if (!cardsByRow.has(top)) {
                    cardsByRow.set(top, []);
                }
                cardsByRow.get(top).push(card);
            });
            
            // Выравниваем высоту карточек в каждой строке
            cardsByRow.forEach(rowCards => {
                if (rowCards.length === 0) return;
                
                // Находим максимальную высоту в этой строке
                let maxHeight = 0;
                rowCards.forEach(card => {
                    const height = card.offsetHeight;
                    if (height > maxHeight) {
                        maxHeight = height;
                    }
                });
                
                // Применяем максимальную высоту ко всем карточкам в строке
                if (maxHeight > 0) {
                    rowCards.forEach(card => {
                        card.style.height = `${maxHeight}px`;
                        card.style.minHeight = `${maxHeight}px`;
                    });
                }
            });
        };
        
        // Обрабатываем карточки в группах отдельно
        const groups = gridRef.current.querySelectorAll(`.${s.groupContainer}`);
        groups.forEach(group => {
            const groupCards = Array.from(group.querySelectorAll(`.${s.messageCard}`));
            alignCardsInRows(groupCards);
        });
        
        // Обрабатываем карточки вне групп
        const cardsOutsideGroups = Array.from(gridRef.current.querySelectorAll(`.${s.messagesGrid} > .${s.messageCard}`));
        alignCardsInRows(cardsOutsideGroups);
    }, [isNova]);

    // Устанавливаем первый раздел как активный при загрузке
    React.useEffect(() => {
        if (enabledSections.length > 0 && !activeSection) {
            setActiveSection(enabledSections[0].id);
        }
    }, [enabledSections, activeSection]);

    // Вычисляем максимальную высоту при изменении активного раздела
    useLayoutEffect(() => {
        if (activeSection) {
            // Используем requestAnimationFrame для синхронного выполнения после рендера
            requestAnimationFrame(() => {
                calculateMaxHeight();
            });
        }
    }, [activeSection, calculateMaxHeight]);

    // Пересчитываем при изменении размера окна
    useEffect(() => {
        const handleResize = () => {
            // Увеличенная задержка для завершения анимации изменения размера
            setTimeout(() => {
                if (activeSection) {
                    calculateMaxHeight();
                }
            }, 200);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeSection, calculateMaxHeight]);

    // Пересчитываем высоты при изменении количества сообщений
    useLayoutEffect(() => {
        if (activeSection && siteSettings?.miniFigma?.sections) {
            const currentSection = siteSettings.miniFigma.sections.find(s => s.id === activeSection);
            if (currentSection) {
                requestAnimationFrame(() => {
                    calculateMaxHeight();
                });
            }
        }
    }, [siteSettings?.miniFigma?.sections, activeSection, calculateMaxHeight]);

    // Хранилище таймеров для каждого сообщения
    const copyTimers = useRef({});
    
    // Функция для плавного закрытия выбора тарифов с анимацией
    const closeTariffSelection = (messageIds = null) => {
        const idsToClose = messageIds || Object.keys(showingTariffSelection).filter(id => showingTariffSelection[id]);
        
        if (idsToClose.length === 0) return;
        
        // Помечаем элементы как закрывающиеся для применения анимации
        const newClosingState = {};
        idsToClose.forEach(id => {
            newClosingState[id] = true;
        });
        setClosingTariffSelection(prev => ({ ...prev, ...newClosingState }));
        
        // После завершения анимации убираем элементы
        setTimeout(() => {
            setShowingTariffSelection(prev => {
                const newState = { ...prev };
                idsToClose.forEach(id => {
                    delete newState[id];
                });
                return newState;
            });
            
            // Убираем состояние закрытия
            setClosingTariffSelection(prev => {
                const newState = { ...prev };
                idsToClose.forEach(id => {
                    delete newState[id];
                });
                return newState;
            });
        }, 200); // Время анимации
    };
    
    // Обработчик кликов вне области для закрытия выбора тарифов
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Проверяем, есть ли открытые выборы тарифов
            const hasOpenSelections = Object.values(showingTariffSelection).some(isOpen => isOpen);
            
            if (!hasOpenSelections) return;
            
            // Проверяем, был ли клик внутри области выбора тарифов
            const clickedInsideTariffSelection = event.target.closest(`[class*="tariffSelection"]`);
            
            // Если клик был вне области выбора тарифов (даже внутри другой карточки), закрываем все
            if (!clickedInsideTariffSelection) {
                closeTariffSelection();
            }
        };
        
        // Добавляем обработчик на документ
        document.addEventListener('mousedown', handleClickOutside);
        
        // Убираем обработчик при размонтировании
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showingTariffSelection]);
    
    // Обработчик клавиши Escape для закрытия выбора тарифов
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                const hasOpenSelections = Object.values(showingTariffSelection).some(isOpen => isOpen);
                if (hasOpenSelections) {
                    closeTariffSelection();
                }
            }
        };
        
        // Добавляем обработчик клавиатуры
        document.addEventListener('keydown', handleEscapeKey);
        
        // Убираем обработчик при размонтировании
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showingTariffSelection]);
    
    // Функция для проверки наличия переменных в тексте
    const hasVariables = (text) => {
        if (!text) return false;
        const variablePattern = /<[A-Z_]+>/g;
        const matches = text.match(variablePattern);
        if (!matches) return false;
        
        // Исключаем переменную DEFAULT_CRYPTO из показа кнопок тарифов
        const filteredMatches = matches.filter(match => match !== '<DEFAULT_CRYPTO>');
        return filteredMatches.length > 0;
    };
    
    // Функция для получения CSS класса тарифа (как в админке)
    const getTariffClass = (tariffLabel) => {
        if (tariffLabel.includes('VIP')) return s.vipTariff;
        if (tariffLabel.includes('1 ТАРИФ')) return s.tariff1;
        if (tariffLabel.includes('2 ТАРИФ')) return s.tariff2;
        if (tariffLabel.includes('3 ТАРИФ')) return s.tariff3;
        if (tariffLabel.includes('4 ТАРИФ')) return s.tariff4;
        return '';
    };
    
    // Функция для определения размера карточки и применения соответствующего класса
    const applyTariffSelectionClass = (cardElement) => {
        if (!cardElement) return;
        
        const rect = cardElement.getBoundingClientRect();
        const width = rect.width;
        
        const tariffSelection = cardElement.querySelector(`.${s.tariffSelection}`);
        if (!tariffSelection) return;
        
        // Удаляем все предыдущие классы размера
        tariffSelection.classList.remove(s.small, s.medium, s.large, s.xlarge);
        
        // Применяем подходящий класс в зависимости от ширины карточки
        if (width < 250) {
            tariffSelection.classList.add(s.small);
        } else if (width < 350) {
            tariffSelection.classList.add(s.medium);
        } else if (width < 500) {
            tariffSelection.classList.add(s.large);
        } else {
            tariffSelection.classList.add(s.xlarge);
        }
    };

    // Функция для обработки клика на карточку
    const handleCardClick = (item, messageId, event) => {
        const fullText = item.text || `${item.title || ''} ${item.subtitle || ''}`;
        
        // Если в тексте есть переменные, показываем выбор тарифов
        if (hasVariables(fullText)) {
            setShowingTariffSelection(prev => {
                const newState = {
                    ...prev,
                    [messageId]: !prev[messageId]
                };
                
                // Если открываем выбор тарифов, применяем подходящий класс
                if (newState[messageId] && event && event.currentTarget) {
                    setTimeout(() => {
                        applyTariffSelectionClass(event.currentTarget);
                    }, 50);
                }
                
                return newState;
            });
        } else {
            // Если переменных нет (или есть только DEFAULT_CRYPTO), копируем с заменой переменных
            const processedText = replaceVariables(fullText);
            copyToClipboard(processedText, messageId);
        }
    };
    
    const fallbackCopy = (text) => {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            return ok;
        } catch (_) {
            return false;
        }
    };

    // Функция для копирования с выбранным тарифом
    const copyWithTariff = async (item, messageId, selectedTariff) => {
        const fullText = item.text || `${item.title || ''} ${item.subtitle || ''}`;
        
        // Заменяем переменные напрямую с выбранным тарифом (без изменения состояния)
        const processedText = replaceVariables(fullText, selectedTariff);
        
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(processedText);
            } else {
                const ok = fallbackCopy(processedText);
                if (!ok) throw new Error('fallback copy failed');
            }
            
            // Очищаем предыдущий таймер для этого сообщения, если он есть
            if (copyTimers.current[messageId]) {
                clearTimeout(copyTimers.current[messageId]);
            }
            
            setCopiedId(messageId);
            
            // Устанавливаем новый таймер на 2 секунды
            copyTimers.current[messageId] = setTimeout(() => {
                setCopiedId(prev => prev === messageId ? null : prev);
                delete copyTimers.current[messageId];
            }, 2000);
            
            // Скрываем выбор тарифов с анимацией
            closeTariffSelection([messageId]);
            
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };
    
    const copyToClipboard = async (text, messageId) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const ok = fallbackCopy(text);
                if (!ok) throw new Error('fallback copy failed');
            }
            
            // Очищаем предыдущий таймер для этого сообщения, если он есть
            if (copyTimers.current[messageId]) {
                clearTimeout(copyTimers.current[messageId]);
            }
            
            setCopiedId(messageId);
            
            // Устанавливаем новый таймер на 2 секунды
            copyTimers.current[messageId] = setTimeout(() => {
                setCopiedId(prev => prev === messageId ? null : prev);
                delete copyTimers.current[messageId];
            }, 2000);
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const toggleSubsection = (subsectionId) => {
        setCollapsedSubsections(prev => ({
            ...prev,
            [subsectionId]: !prev[subsectionId]
        }));
    };

    // Функция для замены переменных на актуальные значения
    const replaceVariables = (text, selectedTariff = null) => {
        if (!text) return text;
        
        const tariff = selectedTariff || activeTariff;
        const currency = siteSettings?.currencyFormat || 'RM';
        
        // Определяем, использовать ли сгенерированные суммы или фиксированные из настроек
        const useGenerated = siteSettings?.randomizeRanges && !siteSettings?.otzovikMode && currentUserGeneratedAmounts && currentUserGeneratedAmounts.tariff === tariff;
        
        // Helper функция для получения значения из generatedAmounts или siteSettings
        const getAmountValue = (fieldKey) => {
            if (useGenerated && currentUserGeneratedAmounts[fieldKey] !== undefined && currentUserGeneratedAmounts[fieldKey] !== null) {
                return currentUserGeneratedAmounts[fieldKey];
            }
            
            // Fallback: используем фиксированные значения из siteSettings
            let settingsKey = fieldKey;
            if (fieldKey === 'tariffCommission') {
                settingsKey = 'tariffCommissions';
            } else if (fieldKey === 'tariffBaseAmount') {
                settingsKey = 'tariffBaseAmounts';
            } else if (fieldKey === 'tariffDepositAmount') {
                settingsKey = 'tariffDepositAmounts';
            } else if (fieldKey === 'tariffVerificationAmount') {
                settingsKey = 'tariffVerificationAmounts';
            } else if (fieldKey === 'tariffAdditionalAmount') {
                settingsKey = 'tariffAdditionalAmounts';
            } else if (fieldKey === 'tariffTaxPercent') {
                settingsKey = 'tariffTaxPercents';
            } else if (fieldKey === 'tariffConversionCommission') {
                settingsKey = 'tariffConversionCommissions';
            } else if (fieldKey === 'tariffBankFee') {
                settingsKey = 'tariffBankFees';
            } else if (fieldKey === 'tariffBrokerFee') {
                settingsKey = 'tariffBrokerFees';
            } else if (fieldKey === 'tariffWinningPercentage') {
                settingsKey = 'tariffWinningPercentages';
            } else {
                settingsKey = fieldKey + 's';
            }
            return siteSettings?.[settingsKey]?.[tariff] ?? 0;
        };
        
        const baseAmount = getAmountValue('tariffBaseAmount') || 7103.84;
        const additionalAmount = getAmountValue('tariffAdditionalAmount') || 3240.56;
        const depositAmount = getAmountValue('tariffDepositAmount') || 169;
        const verifAmount = getAmountValue('tariffVerificationAmount') || 492;
        const taxPercent = getAmountValue('tariffTaxPercent') || 4.4;
        const cryptoConvertAmount = getAmountValue('tariffConversionCommission') || 459;
        const bankConvertAmount = getAmountValue('tariffBankFee') || 385;
        const commissionAmount = getAmountValue('tariffCommission') || 549;
        const brokerFeeAmount = getAmountValue('tariffBrokerFee') || 250;
        const winningPercent = getAmountValue('tariffWinningPercentage') || 15;
        
        const formatAmount = (amount) => {
            if (isNaN(amount) || amount === null || amount === undefined) return '0';
            const num = parseFloat(amount);
            const parts = num.toFixed(2).split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1];
            
            if (siteSettings?.numberFormat === 'dot') {
                // Европейский формат: 1.234,56
                const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                return `${formattedInteger},${decimalPart}`;
            } else {
                // Американский формат: 1,234.56
                const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return `${formattedInteger}.${decimalPart}`;
            }
        };

        // Рассчитываем процент комиссии как в модале
        const commissionPercent = baseAmount > 0 ? ((commissionAmount / baseAmount) * 100).toFixed(1) : 0;
        
        // Сумма для модала верификации должна быть без суммы верификации:
        // Используем только baseAmount + additionalAmount
        const verifyModalAmount = baseAmount + additionalAmount;
        const taxAmount = verifyModalAmount * (taxPercent / 100);
        
        // Получаем криптовалюту по умолчанию из настроек
        const defaultCrypto = siteSettings?.defaultCrypto || 'ETH';
        
        const variables = {
            '<USER_WIN>': `${formatAmount(baseAmount)} ${currency}`,
            '<DEPOSIT_AMOUNT>': `${Math.round(depositAmount)}`,
            '<COMMISSION_SIZE>': `${formatAmount(commissionAmount)} ${currency}`,
            '<VERIFICATION_AMOUNT>': `${formatAmount(verifAmount)} ${currency}`,
            '<ADDITIONAL_AMOUNT>': `${formatAmount(additionalAmount)} ${currency}`,
            '<TAX_PERCENT>': `${taxPercent}%`,
            '<TAX_AMOUNT>': `${formatAmount(taxAmount)} ${currency}`,
            '<CRYPTO_COMMISSION>': `${formatAmount(cryptoConvertAmount)} ${currency}`,
            '<BANK_COMMISSION>': `${formatAmount(bankConvertAmount)} ${currency}`,
            '<COMMISSION_PERCENT>': `${commissionPercent}%`,
            '<BROKER_FEE>': `${formatAmount(brokerFeeAmount)} ${currency}`,
            '<WINNING_PERCENT>': `${winningPercent}%`,
            '<WINNING_AMOUNT>': `${formatAmount(baseAmount * winningPercent / 100)} ${currency} (${winningPercent}%/${formatAmount(baseAmount)} ${currency})`,
            '<DEFAULT_CRYPTO>': defaultCrypto
        };
        
        // Добавляем переменные для кастомных модалов
        if (siteSettings?.modalTemplates) {
            siteSettings.modalTemplates.forEach(template => {
                if (template.financeFieldName && template.actionKey) {
                    const actionKeyCapitalized = template.actionKey.charAt(0).toUpperCase() + template.actionKey.slice(1);
                    const settingsKey = `tariff${actionKeyCapitalized}Amounts`;
                    const rangesKey = `${settingsKey}Ranges`;
                    const fieldKey = `tariff${actionKeyCapitalized}Amount`;
                    
                    // Получаем сумму для кастомного модала
                    let customAmount = 0;
                    if (useGenerated && currentUserGeneratedAmounts?.[fieldKey] !== undefined && currentUserGeneratedAmounts[fieldKey] !== null) {
                        customAmount = currentUserGeneratedAmounts[fieldKey];
                    } else {
                        const fixedAmount = siteSettings?.[settingsKey]?.[tariff];
                        if (fixedAmount !== undefined && fixedAmount !== null && fixedAmount !== 0) {
                            customAmount = fixedAmount;
                        } else {
                            const ranges = siteSettings?.[rangesKey]?.[tariff];
                            if (ranges && ranges.min !== undefined && ranges.max !== undefined) {
                                if (siteSettings?.randomizeRanges && !siteSettings?.otzovikMode) {
                                    customAmount = ranges.min + Math.random() * (ranges.max - ranges.min);
                                } else {
                                    customAmount = (ranges.min + ranges.max) / 2;
                                }
                            }
                        }
                    }
                    
                    // Добавляем переменные: <ACTION_KEY_AMOUNT>, <actionKeyAmount>, <ActionKeyAmount>
                    const varName = `<${template.actionKey.toUpperCase()}_AMOUNT>`;
                    const varNameLower = `<${template.actionKey.toLowerCase()}Amount>`;
                    const varNameUpper = `<${actionKeyCapitalized}Amount>`;
                    variables[varName] = `${formatAmount(customAmount)} ${currency}`;
                    variables[varNameLower] = `${formatAmount(customAmount)} ${currency}`;
                    variables[varNameUpper] = `${formatAmount(customAmount)} ${currency}`;
                }
            });
        }

        let replacedText = text;
        Object.entries(variables).forEach(([variable, value]) => {
            replacedText = replacedText.replace(new RegExp(variable.replace(/[<>]/g, '\\$&'), 'g'), value);
        });

        return replacedText;
    };


    if (enabledSections.length === 0) {
        return null;
    }

    const tabsBar = (
        <div className={s.tabs}>
            {enabledSections.map((section) => (
                <button
                    key={section.id}
                    className={`${s.tab} ${activeSection === section.id ? s.active : ''}`}
                    onClick={() => handleTabChange(section.id)}
                >
                    {section.name}
                </button>
            ))}
        </div>
    );

    return (
        <div ref={rootRef} className={isNova ? `${s.miniFigma} ${s.novaSkin}` : `${s.miniFigma} ${isNavbarCollapsed ? s.navbarCollapsed : s.navbarExpanded}`} data-ltr-force>
            {/* Section Tabs: в Nova язычки порталятся в шапку шита,
                в классике остаются на месте */}
            {isNova
                ? (tabsPortalNode
                    ? ReactDOM.createPortal(
                        <div className={`${s.novaSkin} ${s.tabsDock}`} data-ltr-force>{tabsBar}</div>,
                        tabsPortalNode,
                    )
                    : null)
                : tabsBar}

            {/* Content */}
            <div className={s.tabContent}>
                {enabledSections.map((section) => (
                    activeSection === section.id && (
                        <div key={section.id} className={`${s.sectionContent} ${isTransitioning ? s.transitioning : ''}`}>
                            {/* Messages Grid */}
                            <div 
                                className={s.messagesGrid} 
                                ref={gridRef}
                                style={{ visibility: 'visible' }}
                            >
                                {(section.messages || []).map((item, itemIndex, itemsArr) => {
                                    const divider = novaDividerBefore(itemsArr, itemIndex)
                                        ? <div className={s.novaDivider} aria-hidden="true" />
                                        : null;
                                    // Рендерим группу
                                    if (item.type === 'group') {
                                        return (
                                            <React.Fragment key={item.id}>
                                            {divider}
                                            <div 
                                                className={s.groupContainer}
                                                data-group-id={item.id}
                                                style={isNova ? {
                                                    // Цвет группы из конфига — аккуратным акцентом через токен
                                                    '--group-color': item.color || '#7B7FF2'
                                                } : {
                                                    borderColor: item.color || '#1e40af',
                                                    backgroundColor: `${item.color || '#1e40af'}08`
                                                }}
                                            >
                                                {/* Заголовок группы */}
                                                <div 
                                                    className={s.groupHeader}
                                                    onClick={() => toggleGroupCollapse(item.id)}
                                                    title={collapsedGroups[item.id] ? "Развернуть группу" : "Свернуть группу"}
                                                    style={isNova ? { cursor: 'pointer' } : {
                                                        borderBottomColor: collapsedGroups[item.id] ? 'transparent' : (item.color || '#1e40af'),
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div 
                                                        className={s.groupTitle}
                                                        style={isNova ? undefined : {
                                                            color: item.color || '#1e40af'
                                                        }}
                                                        dangerouslySetInnerHTML={{ 
                                                            __html: convertEmojiToSvg(item.title || '📁 Группа') 
                                                        }}
                                                    />
                                                    {isNova && (
                                                        <span className={s.groupCount}>{(item.messages || []).length}</span>
                                                    )}
                                                    <div
                                                        className={s.groupCollapseBtn}
                                                        style={isNova ? undefined : {
                                                            color: item.color || '#1e40af'
                                                        }}
                                                    >
                                                        {isNova ? (
                                                            <ChevronDown
                                                                size={15}
                                                                strokeWidth={2.1}
                                                                className={`${s.novaChevron} ${collapsedGroups[item.id] ? s.novaChevronCollapsed : ''}`}
                                                                aria-hidden="true"
                                                            />
                                                        ) : (collapsedGroups[item.id] ? '▶' : '▼')}
                                                    </div>
                                                </div>
                                                
                                                {/* Сообщения группы. Nova: grid-обёртка для анимации 0fr↔1fr */}
                                                <div className={`${s.groupMessages} ${collapsedGroups[item.id] ? s.collapsing : ''}`}>
                                                    {(() => {
                                                    const groupCards = (item.messages || []).map((groupMessage) => {
                                                        const { emoji: groupBackgroundEmoji, textWithoutEmoji: groupTextWithoutEmoji } = extractFirstEmoji(groupMessage.title);
                                                        
                                                        return (
                                                            <div
                                                                key={groupMessage.id}
                                                                className={`${s.messageCard} ${groupMessage.fullWidth ? s.fullWidth : ''}`}
                                                                onClick={(e) => handleCardClick(groupMessage, groupMessage.id, e)}
                                                                title={isNova ? undefined : "Нажмите для копирования"}
                                                                style={isNova ? undefined : {
                                                                    borderColor: item.color || '#1e40af'
                                                                }}
                                                            >
                                                                {groupBackgroundEmoji && (
                                                                    <div 
                                                                        className={s.messageEmojiHeader}
                                                                        dangerouslySetInnerHTML={{ __html: convertEmojiToSvg(groupBackgroundEmoji) }}
                                                                    />
                                                                )}
                                                            <div className={s.messageTitle}>
                                                                {replaceVariables(groupTextWithoutEmoji) || 'Без названия'}
                                                            </div>
                                                            {groupMessage.subtitle && (
                                                                <div className={s.messageSubtitle}>
                                                                    {replaceVariables(groupMessage.subtitle)}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Выбор тарифов для сообщений в группе */}
                                                            {showingTariffSelection[groupMessage.id] && (
                                                                <div 
                                                                    className={`${s.tariffSelection} ${closingTariffSelection[groupMessage.id] ? s.closing : ''}`} 
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className={s.tariffButtons}>
                                                                        {Object.keys(siteSettings?.tariffBaseAmounts || {}).map((tariff) => (
                                                                            <button
                                                                                key={tariff}
                                                                                className={`${s.tariffButton} ${getTariffClass(tariff)}`}
                                                                                onClick={() => copyWithTariff(groupMessage, groupMessage.id, tariff)}
                                                                            >
                                                                                <img src={getTariffIcon(tariff)} alt={tariff} className={s.tariffButtonIcon} />
                                                                                <span>{isNova ? getTariffShortName(tariff) : getTariffName(tariff)}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                                
                                                                {isNova && (
                                                                    <span className={s.copyHint} aria-hidden="true">
                                                                        <Copy size={12} strokeWidth={2} />
                                                                    </span>
                                                                )}
                                                                <div className={`${s.copyNotification} ${copiedId === groupMessage.id ? s.show : ''}`}>
                                                                    {isNova ? <Check size={13} strokeWidth={2.6} aria-hidden="true" /> : '✓'} Скопировано
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                    return isNova ? (
                                                        <div className={s.groupMessagesInner}>
                                                            <div className={s.groupMessagesPad}>{groupCards}</div>
                                                        </div>
                                                    ) : groupCards;
                                                    })()}
                                                </div>
                                            </div>
                                            </React.Fragment>
                                        );
                                    }
                                    
                                    // Рендерим обычное сообщение
                                    const { emoji: backgroundEmoji, textWithoutEmoji } = extractFirstEmoji(item.title || item.name);
                                    
                                    return (
                                        <React.Fragment key={item.id}>
                                        {divider}
                                        <div
                                            className={`${s.messageCard} ${item.fullWidth ? s.fullWidth : ''}`}
                                            onClick={(e) => handleCardClick(item, item.id, e)}
                                            title={isNova ? undefined : "Нажмите для копирования"}
                                        >
                                            {backgroundEmoji && (
                                                <div 
                                                    className={s.messageEmojiHeader}
                                                    dangerouslySetInnerHTML={{ __html: convertEmojiToSvg(backgroundEmoji) }}
                                                />
                                            )}
                                            <div className={s.messageTitle}>
                                                {replaceVariables(textWithoutEmoji) || 'Без названия'}
                                            </div>
                                            {item.subtitle && (
                                                <div className={s.messageSubtitle}>
                                                    {replaceVariables(item.subtitle)}
                                                </div>
                                            )}
                                            
                                            {/* Выбор тарифов */}
                                            {showingTariffSelection[item.id] && (
                                                <div 
                                                    className={`${s.tariffSelection} ${closingTariffSelection[item.id] ? s.closing : ''}`} 
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className={s.tariffButtons}>
                                                        {Object.keys(siteSettings?.tariffBaseAmounts || {}).map((tariff) => (
                                                            <button
                                                                key={tariff}
                                                                className={`${s.tariffButton} ${getTariffClass(tariff)}`}
                                                                onClick={() => copyWithTariff(item, item.id, tariff)}
                                                            >
                                                                <img src={getTariffIcon(tariff)} alt={tariff} className={s.tariffButtonIcon} />
                                                                <span>{isNova ? getTariffShortName(tariff) : getTariffName(tariff)}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {isNova && (
                                                <span className={s.copyHint} aria-hidden="true">
                                                    <Copy size={12} strokeWidth={2} />
                                                </span>
                                            )}
                                            <div className={`${s.copyNotification} ${copiedId === item.id ? s.show : ''}`}>
                                                {isNova ? <Check size={13} strokeWidth={2.6} aria-hidden="true" /> : '✓'} Скопировано
                                            </div>
                                        </div>
                                        </React.Fragment>
                                    );
                                })}
                                
                                {/* Empty state */}
                                {(section.messages || []).length === 0 && (
                                    <div className={s.emptyMessage}>
                                        В разделе "{section.name}" пока нет сообщений.<br/>
                                        Добавьте сообщения в настройках.
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                ))}
            </div>

            {/* Кнопка "Наверх" — в Nova не нужна: скролл живёт внутри шита */}
            {!isNova && (
                <button 
                    className={`${s.scrollToTopButton} ${showScrollToTop ? s.visible : ''}`}
                    onClick={scrollToTop}
                    title="Наверх"
                    aria-label="Прокрутить наверх"
                >
                    ↑
                </button>
            )}
        </div>
    );
};

export default MiniFigma;
